import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { first, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { ProductConfigurationOption } from '../../models/product-configuration-types';
import { SelectedCustomerService } from '../selected-customer/selected-customer.service';
import { GungFlowService } from '../gung-flow/gung-flow.service';

@Injectable({
  providedIn: 'root'
})
export class ProductConfigurationOptionPriceService {
  protected http = inject(HttpClient);
  protected selectedCustomerService = inject(SelectedCustomerService);
  protected gungFlowService = inject(GungFlowService);

  protected cachedPrices = new Map<string, OptionPrice>();
  private currentScope = '';

  constructor() {}

  public fetchOptionPrices(optionIds: string[]): Observable<Map<string, OptionPrice>> {
    if (!optionIds || optionIds.length === 0) {
      return of(new Map<string, OptionPrice>());
    }

    return this.getScope().pipe(
      switchMap(({ customerId, flowId, scope }) => {
        const requestBody = optionIds.map(optionId => ({ optionId, customerId, flowId }));

        return this.http
          .post<{ [optionId: string]: OptionPriceResponse }>('json/product-configuration/option-prices', requestBody)
          .pipe(
            map(response => {
              const priceMap = new Map<string, OptionPrice>();
              for (const optionId of Object.keys(response)) {
                priceMap.set(optionId, this.mapOptionPrice(optionId, response[optionId]));
              }
              return priceMap;
            }),
            tap(priceMap => priceMap.forEach((value, key) => this.cachedPrices.set(this.cacheKey(scope, key), value)))
          );
      })
    );
  }

  public getPrice(option: ProductConfigurationOption): Observable<OptionPrice> {
    return this.getScope().pipe(
      switchMap(({ scope }) => {
        const cached = this.cachedPrices.get(this.cacheKey(scope, option.id));
        if (cached) {
          return of(cached);
        }
        return this.fetchOptionPrices([option.id]).pipe(
          map(priceMap => priceMap.get(option.id) || { id: option.id, price: 0, currency: '' })
        );
      })
    );
  }

  public getCachedPrice(optionId: string): OptionPrice | undefined {
    return this.cachedPrices.get(this.cacheKey(this.currentScope, optionId));
  }

  /**
   * Option prices depend on the customer and the flow's pricelist, so the cache is kept per both.
   */
  protected getScope(): Observable<{ customerId: string; flowId: string; scope: string }> {
    return forkJoin([
      this.selectedCustomerService.getSelectedCustomer().pipe(first()),
      this.gungFlowService.getSelectedFlow().pipe(first())
    ]).pipe(
      map(([customer, flow]) => {
        const scope = `${customer?.id}@${flow?.id}`;
        this.currentScope = scope;
        return { customerId: customer?.id, flowId: flow?.id, scope };
      })
    );
  }

  protected cacheKey(scope: string, optionId: string): string {
    return `${scope}@${optionId}`;
  }

  /**
   * The option's price as this shop shows it. Defaults to wsp, the pricelist sum; the response
   * also carries net, the same sum with each material's discount applied, for shops that quote
   * their customers discounted prices.
   */
  protected mapOptionPrice(optionId: string, entry: OptionPriceResponse): OptionPrice {
    return {
      id: optionId,
      price: entry.wsp || 0,
      currency: this.resolveCurrency(entry)
    };
  }

  protected resolveCurrency(entry: OptionPriceResponse): string {
    if (entry.currencyCode) {
      return entry.currencyCode;
    }

    // Fallback: try to derive currency from _includedPrices
    if (entry.extra?._includedPrices) {
      const firstPriceKey = Object.keys(entry.extra._includedPrices)[0];
      if (firstPriceKey) {
        const priceEntry = entry.extra._includedPrices[firstPriceKey];
        if (priceEntry?.extra?.gungPrice?.currencyCode) {
          return priceEntry.extra.gungPrice.currencyCode;
        }
        if (priceEntry?.priceList) {
          return priceEntry.priceList;
        }
      }
    }

    return '';
  }
}

export interface OptionPrice {
  id: string;
  price: number;
  currency: string;
}

export interface OptionPriceResponse {
  featureId: string;
  optionId: string;
  customerId: string;
  /** The option's materials at their pricelist prices, before any discount. */
  wsp: number;
  /** wsp with each material's own discount applied - what the customer actually pays. */
  net?: number;
  currencyCode: string | null;
  extra?: {
    _includedPrices?: {
      [productId: string]: {
        productId: string;
        priceList: string;
        rrp: number;
        wsp: number;
        extra?: {
          gungPrice?: {
            currencyCode: string;
          };
        };
      };
    };
  };
}
